import { buildPagedListPath } from "../../utils/listFilterQuery";

export type NotificationChannel = "email" | "whatsapp" | "";
export type NotificationStatus = "sent" | "failed" | "skipped" | "";
export type AutomationCategory = "business" | "auth" | "";

export type NotificationAutomation = {
  key: string;
  name: string;
  channel: "email" | "whatsapp";
  trigger: string;
  category: "business" | "auth";
};

export type NotificationLogItem = {
  id: number;
  channel: "email" | "whatsapp";
  automation: string;
  recipient: string;
  status: "sent" | "failed" | "skipped";
  subject_or_template: string;
  error_message: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
};

export const NOTIFICATION_CHANNEL_FILTERS = [
  { value: "", label: "Todos" },
  { value: "email", label: "E-mail" },
  { value: "whatsapp", label: "WhatsApp" },
] as const;

export const NOTIFICATION_STATUS_FILTERS = [
  { value: "", label: "Todos" },
  { value: "sent", label: "Enviado" },
  { value: "failed", label: "Falhou" },
  { value: "skipped", label: "Ignorado" },
] as const;

export const AUTOMATION_CATEGORY_FILTERS = [
  { value: "", label: "Todas (incluir OTP)" },
  { value: "business", label: "Só negócio" },
] as const;

export function parseNotificationChannelFilter(
  value: string | null | undefined
): NotificationChannel {
  if (value === "email" || value === "whatsapp") return value;
  return "";
}

export function parseNotificationStatusFilter(
  value: string | null | undefined
): NotificationStatus {
  if (value === "sent" || value === "failed" || value === "skipped") return value;
  return "";
}

export function parseAutomationCategoryFilter(
  value: string | null | undefined
): AutomationCategory {
  if (value === "business" || value === "auth") return value;
  return "";
}

export function parseDateFilter(value: string | null | undefined): string {
  const text = String(value ?? "").trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) return "";
  return text;
}

export function buildNotificationsPath(
  page: number,
  filters: {
    recipient: string;
    channel: NotificationChannel;
    status: NotificationStatus;
    automation: string;
    fromDate: string;
    toDate: string;
  }
): string {
  return buildPagedListPath("/notifications", page, {
    recipient: filters.recipient,
    channel: filters.channel,
    status: filters.status,
    automation: filters.automation,
    from_date: filters.fromDate,
    to_date: filters.toDate,
  });
}

export type AutomationFilterOption = {
  value: string;
  label: string;
  channel?: "email" | "whatsapp";
};

export function buildAutomationOptions(
  automations: NotificationAutomation[]
): AutomationFilterOption[] {
  return [
    { value: "", label: "Todas" },
    ...automations.map((automation) => ({
      value: automation.key,
      label: automation.name,
      channel: automation.channel,
    })),
  ];
}

export type NotificationTab = "history" | "automations";

export const NOTIFICATION_TAB_SWIPE_THRESHOLD_PX = 56;

export function resolveTabAfterSwipe(
  deltaX: number,
  activeTab: NotificationTab,
  threshold = NOTIFICATION_TAB_SWIPE_THRESHOLD_PX
): NotificationTab | null {
  if (Math.abs(deltaX) < threshold) return null;

  if (deltaX > 0 && activeTab === "history") return "automations";
  if (deltaX < 0 && activeTab === "automations") return "history";

  return null;
}

export function resolveDefaultNotificationTab(
  canViewLogs: boolean,
  canViewAutomations: boolean
): NotificationTab {
  if (canViewLogs) return "history";
  if (canViewAutomations) return "automations";
  return "history";
}

export function resolveAutomationName(
  automationKey: string,
  automationsByKey: Map<string, NotificationAutomation>
): string {
  const match = automationsByKey.get(automationKey);
  if (match) return match.name;
  return automationKey;
}
